import { IntegrationCardDetailsText } from '@modules/integrationsV2/components/IntegrationCardDetailsText';
import {
  getIntegrationChannelsMetadata,
  getIntegrationDataLabel,
} from '@modules/integrationsV2/utils';
import { Box, Tooltip, Typography } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationViewFragment,
} from 'generated/graphql';
import { toTitleCase } from 'v2/utils/strings';

interface ConnectedIntegrationSecondaryDetailsProps {
  integration: IntegrationViewFragment;
}

export const ConnectedIntegrationSecondaryDetails = ({
  integration,
}: ConnectedIntegrationSecondaryDetailsProps) => {
  const integrationChannelsMetadata = getIntegrationChannelsMetadata(
    integration.channel.type,
    integration.data,
  );

  if (integration.channel.type === IntegrationChannelType.Email) {
    const email = integration.data?.emailInbound?.receiveOnEmail;
    return email ? (
      <IntegrationCardDetailsText
        showClickToCopyTooltip
        text={email}
        prefixText="Send your feedback to:"
      />
    ) : null;
  }

  if (integration.channel.type === IntegrationChannelType.FacebookPages) {
    const subscribedPage = integration.data?.facebookPages?.subscribedPage;
    return subscribedPage ? (
      <IntegrationCardDetailsText
        showClickToCopyTooltip
        text={subscribedPage.name}
        color="text.secondary"
      />
    ) : (
      <Typography color="text.secondary" variant="caption" noWrap>
        No page added
      </Typography>
    );
  }

  if (integration.channel.type === IntegrationChannelType.Instagram) {
    const subscribedHandles =
      integration.data?.instagram?.subscribedInstagramHandle;

    return subscribedHandles ? (
      <IntegrationCardDetailsText
        showClickToCopyTooltip
        text={subscribedHandles.name}
        color="text.secondary"
      />
    ) : (
      <Typography color="text.secondary" variant="caption" noWrap>
        No handle added
      </Typography>
    );
  }

  if (integration.channel.type === IntegrationChannelType.Reddit) {
    const subreddit = integration.data?.reddit?.subreddit;
    return subreddit ? (
      <IntegrationCardDetailsText
        showClickToCopyTooltip
        text={subreddit}
        color="text.secondary"
      />
    ) : (
      <Typography color="text.secondary" variant="caption" noWrap>
        No subreddit added
      </Typography>
    );
  }

  if (integration.channel.type === IntegrationChannelType.Webhook) {
    const webhookDisplayName = integration.data?.webhook?.displayName;
    return (
      <IntegrationCardDetailsText
        showClickToCopyTooltip
        text={webhookDisplayName}
        color="text.secondary"
      />
    );
  }

  if (!integrationChannelsMetadata) {
    return null;
  }

  const integrationDataLabel = getIntegrationDataLabel(
    integration.channel.type,
  );

  if (integrationChannelsMetadata.length === 0) {
    return (
      <Typography color="text.secondary" variant="caption" noWrap>
        No {integrationDataLabel} added
      </Typography>
    );
  }

  return (
    <Typography color="text.secondary" variant="caption">
      {toTitleCase(integrationDataLabel)} added:{' '}
      <Tooltip
        arrow
        placement="top"
        title={
          <Box maxHeight="120px" sx={{ overflowY: 'auto' }}>
            <Typography variant="caption">
              {integrationChannelsMetadata
                .map((channel) => channel.name)
                .join(', ')}
            </Typography>
          </Box>
        }
      >
        <Typography
          component="span"
          variant="caption"
          sx={{
            color: 'primary.main',
          }}
        >
          {integrationChannelsMetadata.length}
        </Typography>
      </Tooltip>
    </Typography>
  );
};
